import React, { useState, useMemo } from 'react';
import { Modal, Input, Table, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTheme } from '../context/ThemeContext';

const MetricsExplorerModal = ({ visible, onClose, metrics = [], onSelect, loading }) => {
  const { isDark } = useTheme();
  const [search, setSearch] = useState('');

  // Build flat list of unique metric names
  const metricList = useMemo(() => {
    const list = [];
    const seen = new Set();
    metrics.forEach((m) => {
      const name = typeof m === 'string' ? m : (m?.__name__ || m?.name);
      if (!name || seen.has(name)) return;
      seen.add(name);
      list.push({ name });
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [metrics]);

  const filtered = useMemo(() => {
    if (!search.trim()) return metricList;
    const q = search.toLowerCase();
    return metricList.filter((m) => m.name.toLowerCase().includes(q));
  }, [metricList, search]);

  const handleSelect = (name) => {
    onSelect?.(name);
    onClose();
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{text}</code>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Button type="primary" size="small" onClick={() => handleSelect(record.name)}>
          Select
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Metrics explorer"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={720}
      styles={{
        body: {
          background: isDark ? '#0d1117' : '#fff',
          padding: 16,
        },
        header: {
          background: isDark ? '#161b22' : '#fff',
          borderBottom: isDark ? '1px solid #30363d' : '1px solid #f0f0f0',
        },
      }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Input
          bordered={false}
          prefix={<SearchOutlined />}
          placeholder="Search metrics by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 320 }}
        />

        <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#8c8c8c' }}>
          Showing {filtered.length} of {metricList.length} metrics
        </div>

        <Table
          size="small"
          dataSource={filtered}
          columns={columns}
          rowKey="name"
          pagination={{
            defaultPageSize: 100,
            pageSizeOptions: ['50', '100', '200', '500'],
            showSizeChanger: true,
            showTotal: (total) => `Showing ${total} results`,
          }}
          loading={loading}
          locale={{ emptyText: search ? 'No matching metrics' : 'No metrics available' }}
        />
      </Space>
    </Modal>
  );
};

export default MetricsExplorerModal;
